import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import { useHistory } from 'react-router-dom'
import PersonOutlineOutlinedIcon from 'components/material-symbol/icons/PersonOutline'
import RoomOutlinedIcon from 'components/material-symbol/icons/Room'
import EventAvailableOutlinedIcon from 'components/material-symbol/icons/EventAvailable'
import CalendarTodayOutlinedIcon from 'components/material-symbol/icons/CalendarToday'
import CheckIcon from 'components/material-symbol/icons/Check'
import AutoAwesomeIcon from 'components/material-symbol/icons/AutoAwesome'
import DefaultDashboard from './default-dashboard'
import DashboardHeader from './dashboard-header'
import get from 'lodash/get'
import ChoiceStartModal from 'components/choice-start-modal'
import LibraryBooksOutlinedIcon from 'components/material-symbol/icons/LibraryBooks'
import CelebrationOutlinedIcon from 'components/material-symbol/icons/Celebration'
import SchoolOutlinedIcon from 'components/material-symbol/icons/School'
import ArrowForwardIcon from 'components/material-symbol/icons/ArrowForward'
import { TonalButton } from 'components/empty-page-state'
import OnboardingProgressSection, {
  getOnboardingProgress,
} from 'components/onboarding-progress'
import useSettingsStyles from 'pages/pga-coach/settings/settings.styles'
import {
  SettingsLinkRow,
  SettingsSection,
} from 'pages/pga-coach/settings/settings-components'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import {
  getMarketplaceListingReasons,
  hasUpcomingPgaHopeRegistrationOpportunity,
} from 'lib/coach-profile/marketplace-listing'
import MarketplaceListingBanner, {
  MARKETPLACE_BANNER_SOURCE,
} from './marketplace-listing-banner'
import UpgradeSuccessModal from 'components/upgrade-success-modal'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import { useMarketplaceListingTracking } from './marketplace-listing-banner/analytics'
import useDashboardStyles from './styles'

const useStyles = makeStyles(theme => ({
  skipButton: {
    alignSelf: 'flex-start',
  },
  onboardingContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  marketplaceBanner: {
    marginBottom: 0,
  },
}))

const ScheduleDialog = ({ dialogOpen, closeDialog, listItems }) => {
  const history = useHistory()

  return (
    <ChoiceStartModal
      open={dialogOpen}
      onClose={closeDialog}
      title="Add to your Schedule"
      subtitle="What type of offering would you like to add to your calendar?"
      columns={2}
      options={listItems.map(block => ({
        id: block.link,
        icon: block.icon,
        title: block.title,
        description: block.description,
        onClick: () => {
          closeDialog()
          history.push(block.link)
        },
      }))}
    />
  )
}

const buildListItems = coachOnboardingProgress => {
  const isComplete = key => coachOnboardingProgress[key] !== null

  return [
    {
      step: 'create-coach-profile',
      icon: <PersonOutlineOutlinedIcon />,
      primary: 'Create a PGA Coach Profile',
      secondary:
        'Get discovered on PGA.com and let golfers book with you. You can hide your profile anytime.',
      link: '/pga-coach/onboarding/profile/new',
      completed: isComplete('coachProfileCreated'),
    },
    {
      step: 'add-facility',
      icon: <RoomOutlinedIcon />,
      primary: 'Set your Location(s)',
      secondary:
        'Add where you coach so golfers can find you. Mark private clubs as private.',
      link: '/pga-coach/onboarding/facilities/new',
      completed: isComplete('facilityAdded'),
    },
    {
      step: 'add-availability',
      icon: <EventAvailableOutlinedIcon />,
      primary: 'Set your Working Hours',
      secondary:
        "Set when you're available before adding lesson types and offerings.",
      link: '/pga-coach/onboarding/availability',
      completed: isComplete('availabilityAdded'),
    },
    {
      step: 'add-schedule',
      icon: <CalendarTodayOutlinedIcon />,
      primary: 'Add to your Schedule',
      secondary:
        'Create private lessons and group programs that fit your coaching style.',
      completed: isComplete('schedulableCreated'),
      scheduleListItems: [
        {
          title: 'Booking Types',
          description: (
            <>
              Booking Types are offerings for <strong>individuals</strong>. Some
              examples: private coaching sessions, club fittings, swing
              evaluations, and lesson packs.
            </>
          ),
          icon: LibraryBooksOutlinedIcon,
          link: '/pga-coach/onboarding/bookings/booking-types/new',
        },
        {
          title: 'Group Events',
          description: (
            <>
              Group Events are offerings for <strong>groups</strong>, like after
              school clinics, Get Golf Ready, supervised practice, and boot
              camps.
            </>
          ),
          icon: CelebrationOutlinedIcon,
          link: '/pga-coach/onboarding/bookings/events/new',
        },
      ],
    },
  ]
}

const advancedListItems = [
  {
    icon: <AutoAwesomeIcon />,
    primary: 'ADM',
    secondary:
      'Access American Development Model resources to support long-term athlete development.',
    link:
      'https://vt.lightspeedvt.com/share/redirect.cfm?signInURL=https://webservices.lightspeedvt.net/SAML/lsvt/login.aspx?h=auth0.pga.com&dest=https://vt.lightspeedvt.com/reports/certification/',
  },
  {
    icon: <SchoolOutlinedIcon />,
    primary: 'Training',
    secondary:
      'Explore coaching courses, videos, and resources in the Training Center.',
    link: 'https://pgacoach.lightspeedvt.com/',
  },
]

const OnboardingStepRow = ({ item, settingsStyles }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const rowIcon = item.completed ? <CheckIcon /> : item.icon

  const sharedProps = {
    icon: rowIcon,
    iconWrapClassName: item.completed
      ? settingsStyles.rowIconCompleted
      : settingsStyles.rowIconTinted,
    title: item.primary,
    description: item.completed ? undefined : item.secondary,
    completed: item.completed,
    badge: item.completed ? 'Done' : undefined,
    classes: settingsStyles,
    'data-testid': item.step,
  }

  if (item.completed) {
    return <SettingsLinkRow {...sharedProps} />
  }

  if (item.link) {
    return <SettingsLinkRow {...sharedProps} to={item.link} />
  }

  return (
    <>
      <SettingsLinkRow {...sharedProps} onClick={() => setDialogOpen(true)} />
      <ScheduleDialog
        listItems={item.scheduleListItems}
        dialogOpen={dialogOpen}
        closeDialog={() => setDialogOpen(false)}
      />
    </>
  )
}

const OnboardingDashboard = ({
  coachOnboardingProgress,
  marketplaceListingReasons = [],
  upgradeFlow,
  onSkip,
}) => {
  const classes = useStyles()
  const settingsStyles = useSettingsStyles()
  const dashboardStyles = useDashboardStyles()
  const listItems = buildListItems(coachOnboardingProgress)

  return (
    <Box
      className={`${dashboardStyles.mainContent} ${classes.onboardingContent}`}
    >
      <MarketplaceListingBanner
        reasons={marketplaceListingReasons}
        className={classes.marketplaceBanner}
        upgradeFlow={upgradeFlow}
      />
      <OnboardingProgressSection
        coachOnboardingProgress={coachOnboardingProgress}
      />

      <Box className={settingsStyles.section}>
        <Box className={settingsStyles.card}>
          <Box className={settingsStyles.cardBody}>
            {listItems.map(item => (
              <OnboardingStepRow
                key={item.step}
                item={item}
                settingsStyles={settingsStyles}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <SettingsSection eyebrow="Advanced setup" classes={settingsStyles}>
        {advancedListItems.map(item => (
          <SettingsLinkRow
            key={item.primary}
            href={item.link}
            icon={item.icon}
            iconWrapClassName={settingsStyles.rowIconTinted}
            title={item.primary}
            description={item.secondary}
            classes={settingsStyles}
          />
        ))}
      </SettingsSection>

      <TonalButton
        className={classes.skipButton}
        onClick={onSkip}
        startIcon={<ArrowForwardIcon fontSize="small" />}
      >
        Skip to Dashboard
      </TonalButton>
    </Box>
  )
}

const Dashboard = ({ data }) => {
  const [isMarketplaceListingBannerEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_MARKETPLACE_LISTING_BANNER,
  ])
  const coach = get(data, 'currentUser.attributes')
  const { coachOnboardingProgress } = coach
  const slug = coach?.coachProfile?.slug
  const [skipped, setSkipped] = useState(false)
  const { completedCount, totalSteps } = getOnboardingProgress(
    coachOnboardingProgress,
  )
  const showDefaultDashboard = skipped || completedCount === totalSteps
  const listingReasons = isMarketplaceListingBannerEnabled
    ? getMarketplaceListingReasons(coach, { lessonTypes: data?.lessonTypes })
    : []
  const isBannerHiddenForPgaHope = hasUpcomingPgaHopeRegistrationOpportunity(
    data?.currentUser?.groupEvents,
  )
  const marketplaceListingReasons = isBannerHiddenForPgaHope
    ? []
    : listingReasons
  // Owned here so the success event and modal survive the banner unmounting
  // once the coach is on the paid plan.
  const upgradeFlow = useUpgradeFlow('dashboard-marketplace-listing-banner', {
    source: MARKETPLACE_BANNER_SOURCE,
    trackPromptShown: false,
  })
  useMarketplaceListingTracking({
    enabled: isMarketplaceListingBannerEnabled,
    coach,
    reasons: listingReasons,
    isBannerHiddenForPgaHope,
    dashboardVariant: showDefaultDashboard ? 'default' : 'onboarding',
  })

  return (
    <>
      <DashboardHeader
        slug={slug}
        variant={showDefaultDashboard ? 'default' : 'onboarding'}
      />
      {showDefaultDashboard ? (
        <DefaultDashboard
          hasOffering={Boolean(coachOnboardingProgress.schedulableCreated)}
          marketplaceListingReasons={marketplaceListingReasons}
          upgradeFlow={upgradeFlow}
          isGroupEventsLimitReached={Boolean(
            coach.freeTierLimitsReached?.includes('GROUP_EVENTS'),
          )}
        />
      ) : (
        <OnboardingDashboard
          coachOnboardingProgress={coachOnboardingProgress}
          marketplaceListingReasons={marketplaceListingReasons}
          upgradeFlow={upgradeFlow}
          onSkip={() => setSkipped(true)}
        />
      )}
      <UpgradeSuccessModal
        isOpen={upgradeFlow.showSuccessModal}
        onClose={upgradeFlow.handleDismiss}
      />
    </>
  )
}

export default Dashboard
